import { useLocation } from 'react-router-dom';
import { SEARCH_ACTION } from '@client/analytics/redux';
import { useSearchData, useUpdateSearchUrlParams } from '@client/catalog/hooks/index';
import { FILTERS } from '@client/catalog/utils/facets';
import { getFiltersValuesFromSearchParams } from '@client/catalog/utils/filters';
import { ensureArray } from '@client/common/utils/array/array';
import { isDefined } from '@client/common/utils/boolean/isDefined';
import { RangeValueType } from 'b2b-common/core/catalog/Catalog.types';

type UseCatalogFacetFiltersActionsReturnType = {
  handleCategoryChange: (category: string, label: string) => void;
  handleFacetFilterCheckboxChange: (sectionField: string, value: string) => void;
  handleFacetFilterRangeChange: (sectionField: string, value: RangeValueType) => void;
  handleFacetFilterRemove: (sectionField: string, value: string | null | undefined) => void;
  handleFacetFiltersRemoveAll: () => void;
};

export const useCatalogFacetFiltersActions = (
  onFilterChange?: (...args: any[]) => void,
): UseCatalogFacetFiltersActionsReturnType => {
  const { search } = useLocation();
  const { data } = useSearchData();
  const { updateSearchUrlParams } = useUpdateSearchUrlParams();
  const facetFilters = getFiltersValuesFromSearchParams(FILTERS.facetFilters, search);
  const catalogFilters = data?.filters || [];

  const handleCategoryChange = (category: string, label: string): void => {
    onFilterChange?.(FILTERS.category, label || 'Alle Kategorien', SEARCH_ACTION.select);

    updateSearchUrlParams({ [FILTERS.category]: category });
  };

  const handleFacetFilterCheckboxChange = (sectionField: string, value: string): void => {
    const updatedFilters = { ...facetFilters } as Record<string, string[]>;

    const filterSection = updatedFilters[sectionField];

    switch (true) {
      case !filterSection: {
        onFilterChange?.(sectionField, value, SEARCH_ACTION.select);
        updatedFilters[sectionField] = [value];
        break;
      }

      case filterSection?.includes?.(value): {
        onFilterChange?.(sectionField, value, SEARCH_ACTION.deselect);
        updatedFilters[sectionField] = ensureArray(filterSection).filter(
          (selectedFilter) => selectedFilter !== value,
        );
        break;
      }

      default: {
        onFilterChange?.(sectionField, value, SEARCH_ACTION.select);
        if (Array.isArray(updatedFilters[sectionField])) {
          updatedFilters[sectionField].push(value);
        }
        break;
      }
    }

    updateSearchUrlParams({ [FILTERS.facetFilters]: updatedFilters });
  };

  const handleFacetFilterRangeChange = (sectionField: string, value: RangeValueType): void => {
    const correspondingFilter = catalogFilters.find(filter => filter.field === sectionField);
    const min = value.min || correspondingFilter?.slider?.minValue;
    const max = value.max || correspondingFilter?.slider?.maxValue;
    const areValuesSpecified = isDefined(min) && isDefined(max);
    const updatedFilters = { ...facetFilters, [sectionField]: value };

    onFilterChange?.(sectionField, areValuesSpecified && `${min}-${max}`, SEARCH_ACTION.select);
    updateSearchUrlParams({ [FILTERS.facetFilters]: updatedFilters });
  };

  const handleFacetFilterRemove = (sectionField: string, value: string | null | undefined): void => {
    const facetFilter = facetFilters[sectionField];
    const updatedFilters = { ...facetFilters };

    if (Array.isArray(facetFilter)) {
      const updatedValues = facetFilter.filter((filterValue) => filterValue !== value);

      if (!updatedValues.length) {
        delete updatedFilters[sectionField];
      } else {
        updatedFilters[sectionField] = updatedValues;
      }
    } else {
      delete updatedFilters[sectionField];
    }

    updateSearchUrlParams({ [FILTERS.facetFilters]: updatedFilters });
  };

  const handleFacetFiltersRemoveAll = () => updateSearchUrlParams({ [FILTERS.facetFilters]: {} });


  return {
    handleCategoryChange,
    handleFacetFilterCheckboxChange,
    handleFacetFilterRangeChange,
    handleFacetFilterRemove,
    handleFacetFiltersRemoveAll,
  };
};
